import * as React from "react";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { State } from "../reducer";
import { getLogs } from "../action/logsAction";
import BlockUi from "@availity/block-ui";
import { useAppDispatch } from "../store";
import { CircularProgress } from "@mui/material";

// Imports MRT
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';

export const Monitoring: React.FC = () => {
    const dispatch = useAppDispatch();
    
    useEffect(() => {
        dispatch(getLogs());
    }, [dispatch]);

    const logs = useSelector(({ logs }: State) => logs.logs);
    const inProgress = useSelector(({ logs }: State) => logs.inProgress);

    // Fonction de formatage inchangée
    const displayDate = (date: Date): string => {
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }) + ' à ' + date.toLocaleTimeString('fr-FR');
    };

    // Les colonnes communes (Mémoïsées une seule fois)
    const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
        { header: 'Code Technique', accessorKey: 'categorie' },
        { header: 'Code Fonctionnel', accessorKey: 'name' },
        { header: 'Message', accessorKey: 'codeFontionnel' },
        { header: 'Nom de fichier', accessorKey: 'fileName' },
        {
            header: 'Date',
            accessorKey: 'date',
            Cell: ({ cell }) => <label>{displayDate(new Date(cell.getValue<string>()))}</label>
        },
        { header: 'Utilisateur', accessorKey: 'owner' }
    ], []);

    // Nos 4 listes filtrées
    const importCycladeLogs = useMemo(() => 
        logs.filter(log => log.logType === "import_cyclade").sort((a: any, b: any) => b.id - a.id), 
    [logs]);
    
    const verifCycladeLogs = useMemo(() => 
        logs.filter(log => log.logType === "verif_cyclade").sort((a: any, b: any) => b.id - a.id), 
    [logs]);
    
    const verifElibLogs = useMemo(() => 
        logs.filter(log => log.logType === "verif_elib").sort((a: any, b: any) => b.id - a.id), 
    [logs]);
    
    const communLogs = useMemo(() => 
        logs.filter(log => log.logType === "import_elib").sort((a: any, b: any) => b.id - a.id), 
    [logs]);

    // -----------------------------------------------------------------
    // LE MINI-COMPOSANT TABLEAU FACTORISÉ
    // -----------------------------------------------------------------
    const LogTable = ({ data }: { data: any[] }) => {
        const table = useMaterialReactTable({
            columns,
            data: data || [],
            
            // On enlève les fioritures inutiles pour des logs
            enableColumnActions: false, 
            enableColumnFilters: false, 
            enableFullScreenToggle: false,
            enableDensityToggle: false,
            enableHiding: false,
            
            // Pas de sélection de ligne, juste de la lecture
            enableRowSelection: false,
            
            localization: MRT_Localization_FR,
            
            // On active la recherche globale
            enableGlobalFilter: true,
            initialState: {
                showGlobalFilter: true, // Affiche la barre de recherche tout de suite
                density: 'compact',     // Tableaux denses
                pagination: { pageSize: 10, pageIndex: 0 }, // Un peu plus petit par défaut
            },
            defaultColumn: {
                minSize: 40,
                size: 150,
            },
        });

        return <MaterialReactTable table={table} />;
    };

    // -----------------------------------------------------------------
    // LE RENDU PRINCIPAL
    // -----------------------------------------------------------------
    return (
        <div className="monitoring-content" style={{ padding: '2rem' }}>
            <h3 className="h3-title">Monitoring</h3>

            <BlockUi loader={<CircularProgress />} tag="div" blocking={inProgress}>
                
                <div style={{ marginBottom: '2rem' }}>
                    <h4>Import des données Cyclade</h4>
                    <LogTable data={importCycladeLogs} />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <h4>Vérification des données Cyclade</h4>
                    <LogTable data={verifCycladeLogs} />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <h4>Vérification de données elibrairie</h4>
                    <LogTable data={verifElibLogs} />
                </div>

                <div style={{ marginBottom: '2rem' }}>
                    <h4>Import elibrairie</h4>
                    <LogTable data={communLogs} />
                </div>

            </BlockUi>
        </div>
    );
};